// Filtre en pastille des listes (registre des dossiers, facturation) : pointillé au repos,
// doré quand un choix est actif, menu de cases à cocher ou de choix unique.
import { useState } from 'react';
import { cn } from '../../lib/cn';

export function MenuChevron({ open, active }: { open: boolean; active?: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={cn(
        'h-3 w-3 shrink-0 transition-transform duration-150',
        open && 'rotate-180',
        active ? 'text-cime-gold-text' : open ? 'text-[#043429]' : 'text-[#8a928c]',
      )}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MenuMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-[18px] shrink-0 place-items-center rounded-[5px] border-[1.5px]',
        checked ? 'border-[#043429] bg-[#043429] text-white' : 'border-[#d4cfc3] bg-white',
      )}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-3 w-3">
          <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export const MENU_PANEL =
  'absolute top-[38px] z-20 max-h-[min(480px,70vh)] overflow-auto rounded-[14px] border border-[#e6e2d8] bg-[#fffdf8] p-2 shadow-[0_16px_40px_rgba(4,52,41,0.12)]';

export function FilterPill({
  label, options, selected, onChange, mode = 'multi',
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  mode?: 'multi' | 'single';
}) {
  const [open, setOpen] = useState(false);
  const active = selected.length > 0;
  const summary = active
    ? selected.length === 1
      ? `${label} · ${options.find((o) => o.value === selected[0])?.label ?? label}`
      : `${label} · ${selected.length}`
    : label;

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-8 max-w-[250px] items-center gap-[7px] rounded-full border bg-white py-0 pl-3 pr-2.5 text-[13px]',
          open
            ? 'border-solid border-[#043429] text-[#043429]'
            : active
              ? 'border-solid border-[#e0d3a4] bg-cime-gold-bg font-bold text-cime-gold-text'
              : 'border-dashed border-[#d4cfc3] text-[#5c6660] hover:border-[#cfcab8] hover:text-[#1f2823]',
        )}
      >
        <span className="truncate">{summary}</span>
        <MenuChevron open={open} active={active} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={cn(MENU_PANEL, 'left-0 w-[272px]')}>
            <p className="mx-2.5 mb-1 mt-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a928c]">{label}</p>
            {options.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-[#8a928c]">Aucune option</p>
            )}
            {options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'my-px flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm text-[#1f2823]',
                    checked ? 'bg-[rgba(201,168,76,0.16)] font-bold text-[#043429]' : 'hover:bg-[#f4f1e8]',
                  )}
                >
                  <span>{opt.label}</span>
                  <input
                    type={mode === 'single' ? 'radio' : 'checkbox'}
                    name={mode === 'single' ? label : undefined}
                    checked={checked}
                    onClick={(event) => {
                      if (mode !== 'single' || !checked) return;
                      event.preventDefault();
                      onChange([]);
                    }}
                    onChange={() => {
                      if (mode === 'single') onChange(checked ? [] : [opt.value]);
                      else onChange(checked ? selected.filter((v) => v !== opt.value) : [...selected, opt.value]);
                    }}
                    className="sr-only"
                  />
                  <MenuMark checked={checked} />
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
